import React, { useState, useRef } from 'react';
import {
  Container,
  Typography,
  Paper,
  useMediaQuery,
  TextField,
  Box,
  Button,
} from '@mui/material';
import Header1 from '../Header/Header1';
import Footer2 from '../Footer/Footer2';

const salesPartnerAgreementText = `
SALES PARTNER AGREEMENT (INDIVIDUAL)

This Sales Partner Agreement ("Agreement") is made and entered into by and between:

Anthrasync Solutions Private Limited, a company incorporated under the laws of India and having its registered office at No 26, Laskar Hosur Road, Adugodi, Bangalore South, Bengaluru, Karnataka 560030, India, hereinafter referred to as the “Company” or “Platform”,

AND

The individual who wishes to serve as a Sales Partner for the Company (hereinafter referred to as the “Sales Partner” or “you”).

Collectively referred to as the “Parties”, and individually as a “Party”.

1. PURPOSE
The Company operates an AI-powered, real-time knowledge exchange platform where experts conduct live knowledge-sharing sessions. This Agreement outlines the terms under which the Sales Partner will promote the platform and earn commissions for successful paid bookings using their unique referral tools.

2. APPOINTMENT
The Company hereby appoints you as a non-exclusive, independent Sales Partner to market and promote its services in India. You shall use reasonable efforts to refer users and drive bookings on the platform.

3. ELIGIBILITY
To be eligible and remain part of the Sales Partner Program, you must:
- Be 18 years or older.
- Not be currently employed with any other organization during the term of this Agreement.
- Provide a valid PAN for tax compliance.
- Have an Indian bank account for receiving payments.
- Have access to a mobile phone and email.
- Be able to use the internet and the Partner Dashboard.
- Have no criminal history or pending legal cases.
- Abide by ethical practices and avoid deceptive promotion.

The Company reserves the right to verify your background and may reject or terminate participation if these criteria are violated.

4. SALES PARTNER RESPONSIBILITIES
You shall:
- Promote the Company’s platform to individuals and organizations.
- Use only Company-approved promotional materials.
- Avoid making false claims about the platform.
- Use your assigned coupon code or referral link.
- Monitor performance via the dashboard.
- Report misuse or suspicious activity promptly.
- Avoid spamming or unethical marketing.

5. COUPON CODE AND DASHBOARD ACCESS
- You will be issued a unique referral code or link.
- You'll receive dashboard access to track:
  - Referrals/bookings
  - Commission earnings
  - Coupon code performance

6. COMMISSION AND PAYMENTS
- You earn 10% commission on each successful paid booking using your referral.
- Bookings must be completed and not refunded.
- Payouts are processed monthly by the 10th for the prior month.
- Minimum payout threshold is ₹1,000.
- Payments will be made via bank transfer.
- All applicable TDS and legal deductions will apply.

7. TERM AND TERMINATION
- This Agreement is effective from the date above and continues until terminated.
- Either Party may terminate with 15 days’ written notice.
- The Company may terminate immediately for:
  - Breach of terms
  - Fraudulent behavior
  - Misuse of brand
  - Inactivity exceeding 90 consecutive days

8. INDEPENDENT CONTRACTOR
You are an independent contractor and not an employee, agent, or partner of the Company.

9. INTELLECTUAL PROPERTY
All promotional content and intellectual property provided remain property of the Company. You may use them only as authorized.

10. CONFIDENTIALITY
All proprietary information, strategies, data, or internal communication must be kept confidential indefinitely.

11. LIMITATION OF LIABILITY
The Company is not liable for indirect or consequential damages. You agree to indemnify the Company for any damages caused by your actions.

12. DEFAMATION
You shall not make false or defamatory remarks about the Company, its services, staff, or platform. Breach may result in legal action and immediate termination.

13. GOVERNING LAW AND JURISDICTION
This Agreement shall be governed by the laws of India. The courts of Bengaluru, Karnataka shall have exclusive jurisdiction.

14. DISPUTE RESOLUTION & FINAL DECISION
In the event of a dispute, both Parties shall first attempt resolution amicably.
For matters related to:
- Agreement interpretation
- Commission disputes
- Eligibility concerns
- Sales Partner conduct

The Company’s internal decision shall be final and binding, though this does not restrict your legal rights.

15. ENTIRE AGREEMENT
This Agreement constitutes the entire understanding between the Parties and supersedes all prior agreements.
`;

const SalesPartnerAgreement = () => {
  const isMobile = useMediaQuery('(max-width:600px)');
  const [searchTerm, setSearchTerm] = useState('');
  const paperRef = useRef(null);

  const handleSearch = () => {
    if (!searchTerm.trim()) return;

    const element = paperRef.current;
    const regex = new RegExp(searchTerm, 'i');
    const match = salesPartnerAgreementText.match(regex);

    if (match && element) {
      const index = salesPartnerAgreementText.search(regex);
      const beforeMatch = salesPartnerAgreementText.slice(0, index);
      const lines = beforeMatch.split('\n');
      const targetLine = lines.length - 1;
      const lineHeight = 24;
      const scrollTop = targetLine * lineHeight;

      element.scrollTo({ top: scrollTop, behavior: 'smooth' });
    }
  };

  const getHighlightedText = (text, highlight) => {
    if (!highlight.trim()) return text;
    const parts = text.split(new RegExp(`(${highlight})`, 'gi'));
    return parts.map((part, i) =>
      part.toLowerCase() === highlight.toLowerCase() ? (
        <mark key={i} style={{ backgroundColor: '#ffe600', color: '#000' }}>
          {part}
        </mark>
      ) : (
        part
      )
    );
  };

  return (
    <div style={{ backgroundColor: '#000', minHeight: '100vh' }}>
      <Header1 />

      <Container maxWidth="md" sx={{ mt: 5, mb: 5 }}>
        <Typography
          variant={isMobile ? 'h5' : 'h4'}
          gutterBottom
          sx={{
            fontWeight: 600,
            textAlign: 'center',
            fontFamily: 'Verdana, sans-serif',
            color: '#ffffff',
          }}
        >
          Sales Partner Agreement (Individual)
        </Typography>

        <Box
          display="flex"
          flexDirection={isMobile ? 'column' : 'row'}
          alignItems={isMobile ? 'stretch' : 'center'}
          gap={2}
          mb={2}
        >
          <TextField
            label="Search"
            variant="outlined"
            fullWidth
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{
              backgroundColor: '#1a1a1a',
              input: { color: '#fff' },
              label: { color: '#ffffff' },
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: '#555' },
                '&:hover fieldset': { borderColor: '#888' },
                '&.Mui-focused fieldset': { borderColor: '#fff' },
              },
            }}
          />
          <Button
            variant="contained"
            onClick={handleSearch}
            sx={{
              fontFamily: 'Verdana, sans-serif',
              bgcolor: '#333',
              color: '#fff',
              '&:hover': { bgcolor: '#555' },
            }}
          >
            Search
          </Button>
        </Box>

        <Paper
          ref={paperRef}
          elevation={4}
          sx={{
            p: { xs: 2, sm: 3 },
            maxHeight: '75vh',
            overflowY: 'auto',
            whiteSpace: 'pre-line',
            backgroundColor: '#080808',
            color: '#e0e0e0',
            borderRadius: 2,
            fontFamily: 'Verdana, sans-serif',
            lineHeight: 1.6,
            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.5)',
            scrollbarWidth: 'thin',
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#555',
              borderRadius: '3px',
            },
          }}
        >
          <Typography variant="body2" component="div" sx={{ fontFamily: 'Verdana, sans-serif' }}>
            {getHighlightedText(salesPartnerAgreementText, searchTerm)}
          </Typography>
        </Paper>
      </Container>

      <Footer2 />
    </div>
  );
};

export default SalesPartnerAgreement;
